/**
 * Water plane with dynamic wave shader and foam effects
 * Enhanced for visual appeal matching reference art style
 */

import * as THREE from 'three';

const waterVertexShader = `
varying vec2 vUv;
varying vec3 vWorldPos;
uniform float uTime;

void main() {
  vUv = uv;

  // Create gentle wave displacement
  vec3 pos = position;
  float waveHeight = 0.15;
  float wave1 = sin(pos.x * 0.02 + uTime * 0.5) * waveHeight;
  float wave2 = sin(pos.y * 0.015 + uTime * 0.3) * waveHeight * 0.7;
  float wave3 = cos((pos.x + pos.y) * 0.01 + uTime * 0.4) * waveHeight * 0.5;
  pos.z += wave1 + wave2 + wave3;

  vWorldPos = (modelMatrix * vec4(pos, 1.0)).xyz;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(pos, 1.0);
}
`;

const waterFragmentShader = `
varying vec2 vUv;
varying vec3 vWorldPos;
uniform float uTime;
uniform vec3 uDeepColor;
uniform vec3 uShallowColor;
uniform vec3 uFoamColor;
uniform vec3 uHighlightColor;

// Simplex noise function for water patterns
vec3 mod289(vec3 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
vec2 mod289(vec2 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
vec3 permute(vec3 x) { return mod289(((x*34.0)+1.0)*x); }

float snoise(vec2 v) {
  const vec4 C = vec4(0.211324865405187, 0.366025403784439, -0.577350269189626, 0.024390243902439);
  vec2 i  = floor(v + dot(v, C.yy));
  vec2 x0 = v - i + dot(i, C.xx);
  vec2 i1 = (x0.x > x0.y) ? vec2(1.0, 0.0) : vec2(0.0, 1.0);
  vec4 x12 = x0.xyxy + C.xxzz;
  x12.xy -= i1;
  i = mod289(i);
  vec3 p = permute(permute(i.y + vec3(0.0, i1.y, 1.0)) + i.x + vec3(0.0, i1.x, 1.0));
  vec3 m = max(0.5 - vec3(dot(x0,x0), dot(x12.xy,x12.xy), dot(x12.zw,x12.zw)), 0.0);
  m = m*m; m = m*m;
  vec3 x = 2.0 * fract(p * C.www) - 1.0;
  vec3 h = abs(x) - 0.5;
  vec3 ox = floor(x + 0.5);
  vec3 a0 = x - ox;
  m *= 1.79284291400159 - 0.85373472095314 * (a0*a0 + h*h);
  vec3 g;
  g.x = a0.x * x0.x + h.x * x0.y;
  g.yz = a0.yz * x12.xz + h.yz * x12.yw;
  return 130.0 * dot(m, g);
}

void main() {
  // Distance from center for depth gradient
  float dist = length(vWorldPos.xz) / 150.0;
  float depthFactor = smoothstep(0.0, 1.0, dist);

  // Dynamic wave patterns
  vec2 uv1 = vWorldPos.xz * 0.015 + uTime * 0.02;
  vec2 uv2 = vWorldPos.xz * 0.025 - uTime * 0.015;
  vec2 uv3 = vWorldPos.xz * 0.008 + vec2(uTime * 0.01, -uTime * 0.008);

  float wave1 = snoise(uv1) * 0.5 + 0.5;
  float wave2 = snoise(uv2) * 0.5 + 0.5;
  float wave3 = snoise(uv3) * 0.5 + 0.5;

  float combinedWaves = (wave1 + wave2 * 0.5 + wave3 * 0.3) / 1.8;

  // Base water color - blend between shallow and deep
  vec3 waterColor = mix(uShallowColor, uDeepColor, depthFactor * 0.7 + combinedWaves * 0.2);

  // Add caustic-like patterns
  float caustic = snoise(vWorldPos.xz * 0.05 + uTime * 0.1) * 0.5 + 0.5;
  caustic = pow(caustic, 2.0) * 0.15;
  waterColor += uHighlightColor * caustic * (1.0 - depthFactor * 0.5);

  // Wave highlights (foam on wave crests)
  float waveHighlight = smoothstep(0.6, 0.8, combinedWaves);
  waterColor = mix(waterColor, uFoamColor, waveHighlight * 0.3);

  // Shore foam effect (stronger near center/land)
  float shoreFoam = 1.0 - smoothstep(0.5, 1.2, dist);
  float foamPattern = snoise(vWorldPos.xz * 0.08 + uTime * 0.05);
  foamPattern = smoothstep(0.2, 0.6, foamPattern);
  waterColor = mix(waterColor, uFoamColor, shoreFoam * foamPattern * 0.4);

  // Subtle specular highlight
  float specular = pow(max(0.0, combinedWaves), 8.0) * 0.2;
  waterColor += vec3(specular);

  // Transparency varies with depth
  float alpha = 0.85 + depthFactor * 0.1;

  gl_FragColor = vec4(waterColor, alpha);
}
`;

export class WaterPlane {
  public readonly mesh: THREE.Mesh;
  public readonly backgroundMesh: THREE.Mesh;
  private material: THREE.ShaderMaterial;

  constructor() {
    // Higher resolution for wave displacement
    const geometry = new THREE.PlaneGeometry(10000, 10000, 128, 128);

    this.material = new THREE.ShaderMaterial({
      uniforms: {
        uTime: { value: 0 },
        uDeepColor: { value: new THREE.Color('#1a5276') },
        uShallowColor: { value: new THREE.Color('#5dade2') },
        uFoamColor: { value: new THREE.Color('#ecf0f1') },
        uHighlightColor: { value: new THREE.Color('#85c1e9') },
      },
      vertexShader: waterVertexShader,
      fragmentShader: waterFragmentShader,
      transparent: true,
      depthWrite: true,
      side: THREE.DoubleSide,
    });

    this.mesh = new THREE.Mesh(geometry, this.material);
    this.mesh.rotation.x = -Math.PI / 2;
    this.mesh.position.y = -0.1;
    this.mesh.receiveShadow = true;
    // Disable fog for water so it's always visible at edges
    this.material.fog = false;

    // Add a solid background plane below the water to prevent seeing through
    const bgGeometry = new THREE.PlaneGeometry(20000, 20000);
    const bgMaterial = new THREE.MeshBasicMaterial({
      color: 0x1a5276,
      side: THREE.DoubleSide,
    });
    this.backgroundMesh = new THREE.Mesh(bgGeometry, bgMaterial);
    this.backgroundMesh.rotation.x = -Math.PI / 2;
    this.backgroundMesh.position.y = -5; // Below the water
    (bgMaterial as THREE.MeshBasicMaterial).fog = false;
  }

  /**
   * Update water animation
   */
  update(deltaTime: number): void {
    this.material.uniforms.uTime.value += deltaTime;
  }

  /**
   * Set water colors for different themes/moods
   */
  setColors(deep: string, shallow: string, foam: string, highlight: string): void {
    this.material.uniforms.uDeepColor.value.set(deep);
    this.material.uniforms.uShallowColor.value.set(shallow);
    this.material.uniforms.uFoamColor.value.set(foam);
    this.material.uniforms.uHighlightColor.value.set(highlight);
  }

  dispose(): void {
    this.mesh.geometry.dispose();
    this.material.dispose();
    this.backgroundMesh.geometry.dispose();
    (this.backgroundMesh.material as THREE.Material).dispose();
  }
}
